/**
 * Queue API types — mirror the mobile app's queueTypes (same backend contract).
 */

export type QueueStatus = 'active' | 'inactive' | 'pending_activation';
export type QueueBookingMode = 'public' | 'controlled_public' | 'private';
export type DayOfWeek = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

export interface QueueProvider {
  id: string;
  user_id: string;
  display_name: string | null;
  initials: string;
  profile_image_url: string | null;
}

export interface QueueSchedule {
  day: DayOfWeek;
  is_enabled: boolean;
  start_time: string | null; // HH:mm
  end_time: string | null;
  max_tokens: number | null;
  time_per_token_minutes: number | null;
}

export interface DayScheduleInput {
  day: DayOfWeek;
  is_enabled: boolean;
  start_time?: string;
  end_time?: string;
  max_tokens?: number;
  time_per_token_minutes?: number;
}

export interface Queue {
  id: string;
  name: string;
  description: string | null;
  status: QueueStatus;
  booking_mode: QueueBookingMode;
  providers: QueueProvider[];
  schedule: QueueSchedule[];
  schedule_summary: string;
  tokens_booked_today: number;
  max_tokens_today: number;
  current_token_number: string | null;
  today_availability: 'available' | 'holiday' | 'not_available';
  average_rating: number;
  total_ratings: number;
  /** Consultation/prescription feature: true only when the business's category
   *  has consultation enabled AND it holds a prescription plan or free trial. */
  consultation_enabled: boolean;
  created_at: string;
  activated_at: string | null;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface CreateQueueRequest {
  name: string;
  provider_ids: string[]; // team member user_ids
  schedule: DayScheduleInput[];
  same_for_all_days?: boolean;
  booking_mode?: QueueBookingMode;
}

export interface UpdateQueueRequest {
  name?: string;
  provider_ids?: string[];
  schedule?: DayScheduleInput[];
  same_for_all_days?: boolean;
  update_existing_slots?: boolean;
  cancel_exceeding_tokens?: boolean;
  booking_mode?: QueueBookingMode;
}

export interface BookingWindow {
  queue_id: string;
  window_date: string; // YYYY-MM-DD
  is_open: boolean;
  opened_at: string | null;
  closed_at: string | null;
}

// ---- display config ----
export const BOOKING_MODES: { id: QueueBookingMode; label: string; icon: string; desc: string }[] = [
  { id: 'public', label: 'Public', icon: 'public', desc: 'Customers can book anytime within scheduled hours.' },
  { id: 'controlled_public', label: 'Public (Self-controlled)', icon: 'touch_app', desc: 'Customers book only when you tap Start for the day.' },
  { id: 'private', label: 'Private', icon: 'lock', desc: 'Only you create tokens. Customers cannot self-book.' },
];

export const DAYS: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const STATUS_CHIP: Record<QueueStatus, { label: string; cls: 'success' | 'default' | 'warning' }> = {
  active: { label: 'Active', cls: 'success' },
  inactive: { label: 'Inactive', cls: 'default' },
  pending_activation: { label: 'Pending Activation', cls: 'warning' },
};

/** Capacity (customer-facing demand) from today's booking ratio. */
export function capacityOf(q: Queue): { pct: number; level: 'low' | 'medium' | 'high'; label: string } {
  const pct = q.max_tokens_today ? Math.min(100, Math.round((q.tokens_booked_today / q.max_tokens_today) * 100)) : 0;
  if (pct > 80) return { pct, level: 'high', label: 'High demand' };
  if (pct >= 50) return { pct, level: 'medium', label: 'Medium demand' };
  return { pct, level: 'low', label: 'Low demand' };
}
